import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Button, InputAdornment, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSearchParams } from "react-router-dom";
import AlertManager from "../../shared/Alert/Alert";
import classes from "./RulesPage.module.css";
import uploadClasses from "../LandedCostUpload/LandedCostUpload.module.css";
import { FILE_ACCEPTED_TYPES_WITH_CSV } from "../LandedCostUpload/LandedCostUploadConstants";
import LandingRuleLogTable from "./LandingRuleLogTable";
import PrioritizationRuleLogTable from "./PrioritizationRuleLogTable";
import MarginRuleLogTable from "./MarginRuleLogTable";
import { getActiveRuleFlag, getRuleLogs, updateRuleFlag, getActiveMarginRules, updateMarginRule } from "../../API/ruleLogsApi";
import { getSinglePriceRowsPage, uploadMarginRuleFile, uploadSinglePriceFile, downloadMarginRuleTemplate, downloadSinglePriceTemplate } from "../../API/marginRuleApi";
import { useMarginRuleOptions } from "./useMarginRuleOptions";

const RULES = [
  { key: "landing", title: "Landing Rules" },
  { key: "prioritization", title: "Prioritization Rules"},
  { key: "margin", title: "Margin Strategy Rules" },
  { key: "singlePrice", title: "Single Price Rules" },
];

const ACTIVE_RULE_PARAM = "rule";

const getActiveRuleFromSearchParams = (searchParams) => {
  const requestedRule = searchParams.get(ACTIVE_RULE_PARAM);
  return RULES.some(({ key }) => key === requestedRule) ? requestedRule : "landing";
};

const FORM_CONFIG = {
  landing: {
    selectOptions: [
      {
        value: "container",
        label: "Change in container rate * container size",
        aliases: [
          "Threshold breach for (container rate * container size)",
          "Threshold breach for (containter rate *container size) rule",
        ],
        unit: "%",
        step: 1,
      },
      {
        value: "variability",
        label: "Global variability rule",
        unit: "%",
        step: 1,
      },
    ],
  },
  prioritization: {
    selectOptions: [
      {
        value: "ratio",
        label: "Recalculated Landed / Old Landed > abs(5%) - percentage",
        unit: "%",
        defaultValue: 0,
        step: 1,
      },
      {
        value: "deltaDollar",
        label: "Recalculated Landed - Old Landed > abs($5,000 per SKU) - dollar",
        unit: "$",
        defaultValue: 0,
        step: 100,
      },
      {
        value: "marginDelta",
        label: "New Margin - Old margin > abs(100bps) - bps",
        unit: "bps",
        defaultValue: 0,
        step: 5,
      },
    ],
  },
};

const MARGIN_SCOPE_OPTIONS = [
  { value: "none", label: "Select Scope" },
  { value: "category", label: "Category" },
  { value: "hq", label: "HQ" },
  { value: "item", label: "Item" },
  { value: "group_code", label: "Group Code" },
];

const MARGIN_STRATEGY_OPTIONS = [
  { value: "maintainMargin", label: "Maintain margin" },
  { value: "maintainPrice", label: "Maintain price" },
  { value: "gpNeutral", label: "GP $ neutral" },
];

const MARGIN_UPLOAD_ALLOWED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const MARGIN_UPLOAD_MAX_SIZE_MB = 50;
const MARGIN_UPLOAD_MAX_SIZE = MARGIN_UPLOAD_MAX_SIZE_MB * 1024 * 1024;

const validateMarginUploadFile = (file) => {
  if (!file) {
    return null;
  }

  const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
  if (!MARGIN_UPLOAD_ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return "Please upload an Excel or CSV file (.xlsx, .xls, or .csv).";
  }

  if (file.size > MARGIN_UPLOAD_MAX_SIZE) {
    return "File size exceeds 50 MB. Please upload a smaller file.";
  }

  return null;
};

const getFirstNumericValue = (candidates = []) => {
  for (const candidate of candidates) {
    const numericCandidate = Number(candidate);
    if (Number.isFinite(numericCandidate)) {
      return numericCandidate;
    }
  }
  return null;
};

const getNumericValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getDefaultValuesByRule = (options = []) =>
  options.reduce((acc, option) => {
    const numericDefault = getNumericValue(option?.defaultValue);
    if (option?.value) {
      acc[option.value] = numericDefault ?? 0;
    }
    return acc;
  }, {});

const getFirstIdValue = (candidates = []) => {
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && candidate !== "") {
      return candidate;
    }
  }
  return null;
};

const getMarginScopeField = (scope) => (scope === "item" ? "itemSku" : "scopeValue");

const getMarginScopeValue = (marginState = {}) => marginState?.[getMarginScopeField(marginState.scope)] || "";

const getMarginScopeOptionValue = (option) => {
  if (typeof option === "string") {
    return option.trim();
  }

  if (option && typeof option === "object") {
    return (option.value || option.label || "").trim();
  }

  return "";
};

const getMarginScopeOptionLabel = (option) => {
  if (typeof option === "string") {
    return option.trim();
  }

  if (option && typeof option === "object") {
    return (option.label || option.value || "").trim();
  }

  return "";
};

const getMarginScopeLabel = (scope) => {
  if (scope === "category") {
    return "Category";
  }
  if (scope === "hq") {
    return "HQ";
  }
  if (scope === "item") {
    return "Item SKU";
  }
  if (scope === "group_code") {
    return "Group Code";
  }
  return "Scope Value";
};

const normalizeRuleText = (value) =>
  typeof value === "string" ? value.toLowerCase().replace(/[^a-z0-9]+/g, "") : "";

const isActiveHistoryRecord = (record) => {
  const activeValue = record?.active ?? record?.isActive ?? record?.status;

  return (
    activeValue === true ||
    activeValue === 1 ||
    activeValue === "1" ||
    activeValue === "true" ||
    activeValue === "TRUE" ||
    activeValue === "active" ||
    activeValue === "ACTIVE" ||
    activeValue === "yes" ||
    activeValue === "YES" ||
    activeValue === "Y"
  );
};

const extractRuleRecords = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === "object") {
    const candidates = ["queryResults", "elements", "data", "rows", "content"];
    for (const key of candidates) {
      if (Array.isArray(payload[key])) {
        return payload[key];
      }
    }
  }
  return payload ? [payload] : [];
};

const normalizeRuleConfig = (ruleKey, payload) => {
  const options = FORM_CONFIG[ruleKey]?.selectOptions || [];
  if (!options.length) {
    return null;
  }

  const records = extractRuleRecords(payload);
  const fallbackOption = options[0];
  const valuesByRule = getDefaultValuesByRule(options);
  const seqIdByRule = {};
  let seqId = null;
  const savedValuesByRule = {};
  const ruleNameByRule = {};
  const activeValuesSeen = new Set();
  let matchedOptionValue = null;
  let activeOptionValue = null;

  records.forEach((record) => {
    if (!record || typeof record !== "object") {
      return;
    }

    const rawOption =
      record.ruleKey ||
      record.ruleName ||
      record.flagName ||
      record.ruleFlag ||
      record.flag ||
      record.name ||
      record.type;

    const normalizedRawOption = normalizeRuleText(rawOption);
    const normalizedOption =
      normalizedRawOption &&
      options.find(
        (option) =>
          normalizeRuleText(option.value) === normalizedRawOption ||
          normalizeRuleText(option.label) === normalizedRawOption ||
          (Array.isArray(option.aliases) &&
            option.aliases.some((alias) => normalizeRuleText(alias) === normalizedRawOption))
      );

    const recordSeqId =
      getFirstNumericValue([
        record.seqId,
        record.id,
        record.ruleId,
        record.recordId,
        record.logId,
        record.ruleLogId,
      ]) ?? getFirstIdValue([record.seqId, record.id, record.ruleId, record.recordId]);

    if (recordSeqId !== null && seqId === null) {
      seqId = recordSeqId;
    }

    const resolvedOption = normalizedOption || (!rawOption && ruleKey === "prioritization" ? fallbackOption : null);

    if (!resolvedOption) {
      return;
    }

    const numericValue =
      getFirstNumericValue([
        record.valueText,
        record.threshold,
        record.ruleValue,
        record.ruleThreshold,
        record.thresholdValue,
        record.amount,
      ]) ??
      resolvedOption?.defaultValue ??
      valuesByRule[resolvedOption.value] ??
      0;

    const shouldUseRecord = ruleKey === "prioritization" ? isActiveHistoryRecord(record) : true;
    if (shouldUseRecord && (!activeValuesSeen.has(resolvedOption.value) || ruleKey !== "prioritization")) {
      valuesByRule[resolvedOption.value] = numericValue;
      activeValuesSeen.add(resolvedOption.value);
    }

    if (recordSeqId !== null) {
      seqIdByRule[resolvedOption.value] = recordSeqId;
    }

    if (!matchedOptionValue && shouldUseRecord) {
      matchedOptionValue = resolvedOption.value;
    }

    if (ruleKey === "prioritization" && isActiveHistoryRecord(record) && !activeOptionValue) {
      activeOptionValue = resolvedOption.value;
    }

    if (shouldUseRecord && !Object.prototype.hasOwnProperty.call(savedValuesByRule, resolvedOption.value)) {
      savedValuesByRule[resolvedOption.value] = numericValue;
      ruleNameByRule[resolvedOption.value] = record.ruleName || resolvedOption.label || resolvedOption.value;
    }
  });

  const selectedOption =
    ruleKey === "prioritization"
      ? fallbackOption?.value || activeOptionValue || matchedOptionValue || ""
      : activeOptionValue || matchedOptionValue || fallbackOption?.value || "";
  const value =
    valuesByRule[selectedOption] ?? getFirstNumericValue([fallbackOption?.defaultValue]) ?? 0;

  return {
    selectedOption,
    value,
    valuesByRule,
    seqIdByRule,
    seqId,
    savedValuesByRule,
    ruleNameByRule,
  };
};

const RulesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeRule, setActiveRule] = useState(() => getActiveRuleFromSearchParams(searchParams));
  const marginFileInputRef = useRef(null);
  const singlePriceFileInputRef = useRef(null);

  useEffect(() => {
    const nextActiveRule = getActiveRuleFromSearchParams(searchParams);
    setActiveRule((currentActiveRule) =>
      currentActiveRule === nextActiveRule ? currentActiveRule : nextActiveRule
    );
  }, [searchParams]);

  const initialState = useMemo(() => {
    const state = {};
    RULES.forEach(({ key }) => {
      if (key === "margin") {
        state[key] = {
          scope: "none",
          scopeValue: null,
          itemSku: null,
          strategy: "maintainMargin",
        };
      } else {
        const config = FORM_CONFIG[key];
        const firstOption = config?.selectOptions?.[0];
        const valuesByRule = getDefaultValuesByRule(config?.selectOptions);
        const selectedOption = firstOption?.value || "";
        state[key] = {
          selectedOption,
          value:
            (selectedOption ? valuesByRule[selectedOption] : getNumericValue(firstOption?.defaultValue)) ||
            0,
          valuesByRule,
          seqIdByRule: {},
          seqId: null,
          savedValuesByRule: {},
          ruleNameByRule: {},
        };
      }
    });
    return state;
  }, []);

  const [formState, setFormState] = useState(initialState);
  const [marginUploadName, setMarginUploadName] = useState("");
  const [singlePriceUploadName, setSinglePriceUploadName] = useState("");
  const [marginSearchTerm, setMarginSearchTerm] = useState("");
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [downloadingSinglePriceTemplate, setDownloadingSinglePriceTemplate] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingSinglePriceFile, setUploadingSinglePriceFile] = useState(false);
  const [loadingRuleConfig, setLoadingRuleConfig] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [logsRefreshKey, setLogsRefreshKey] = useState(0);
  const [prioritizationLogRows, setPrioritizationLogRows] = useState([]);
  const [marginInputValue, setMarginInputValue] = useState("");
  const [marginStrategies, setMarginStrategies] = useState([]);
  const [marginPage, setMarginPage] = useState(0);
  const [marginTotalPages, setMarginTotalPages] = useState(0);
  const [marginTotalElements, setMarginTotalElements] = useState(0);
  const [singlePriceRows, setSinglePriceRows] = useState([]);
  const [singlePricePage, setSinglePricePage] = useState(0);
  const [singlePriceTotalPages, setSinglePriceTotalPages] = useState(0);
  const [singlePriceTotalElements, setSinglePriceTotalElements] = useState(0);

  const loadRuleConfig = useCallback(async (ruleKey, preferredSelectedOption) => {
    if (!FORM_CONFIG[ruleKey]) {
      return;
    }
    setLoadingRuleConfig(true);
    try {
      const activeRuleConfig =
        ruleKey === "prioritization" ? await getRuleLogs(ruleKey) : await getActiveRuleFlag(ruleKey);
      if (ruleKey === "prioritization") {
        setPrioritizationLogRows(extractRuleRecords(activeRuleConfig));
      }
      const normalized = normalizeRuleConfig(ruleKey, activeRuleConfig);
      if (normalized) {
        const ruleOptions = FORM_CONFIG[ruleKey]?.selectOptions || [];
        setFormState((prev) => {
          const preferredOption =
            preferredSelectedOption &&
            ruleOptions.some((option) => option.value === preferredSelectedOption) &&
            Object.prototype.hasOwnProperty.call(normalized.valuesByRule || {}, preferredSelectedOption)
              ? preferredSelectedOption
              : null;

          const nextSelectedOption = preferredOption || normalized.selectedOption;
          const normalizedValuesByRule = normalized.valuesByRule || {};
          const normalizedValue = getNumericValue(normalizedValuesByRule[nextSelectedOption]);
          const previousValue = getNumericValue(prev[ruleKey]?.valuesByRule?.[nextSelectedOption]);
          const nextValue = normalizedValue ?? previousValue ?? normalized.value;

          return {
            ...prev,
            [ruleKey]: {
              ...(prev[ruleKey] || {}),
              ...normalized,
              selectedOption: nextSelectedOption,
              value: nextValue,
              seqId: normalized.seqId ?? prev[ruleKey]?.seqId ?? null,
              seqIdByRule: { ...(prev[ruleKey]?.seqIdByRule || {}), ...(normalized.seqIdByRule || {}) },
              savedValuesByRule: {
                ...(prev[ruleKey]?.savedValuesByRule || {}),
                ...(normalized.savedValuesByRule || {}),
              },
              ruleNameByRule: {
                ...(prev[ruleKey]?.ruleNameByRule || {}),
                ...(normalized.ruleNameByRule || {}),
              },
            },
          };
        });
      }
    } finally {
      setLoadingRuleConfig(false);
    }
  }, []);

  const loadMarginRules = useCallback(async (nextPage = 0) => {
    setLoadingRuleConfig(true);
    try {
      const activeMarginRules = await getActiveMarginRules({ page: nextPage, size: 10 });
      const marginRows = Array.isArray(activeMarginRules?.rows) ? activeMarginRules.rows : [];
      setMarginStrategies(marginRows);
      setMarginPage(Number(activeMarginRules?.currentPage ?? nextPage));
      setMarginTotalPages(Number(activeMarginRules?.totalPages ?? 0));
      setMarginTotalElements(Number(activeMarginRules?.totalElements ?? marginRows.length));
      if (marginRows.length > 0) {
        // Map strategy from UPPER_CASE to camelCase
        const strategyMap = {
          GP_NEUTRAL: "gpNeutral",
          MAINTAIN_PRICE: "maintainPrice",
          MAINTAIN_MARGIN: "maintainMargin",
          SINGLE_PRICE: "singlePrice",
        };

        // Process active rules and set default values
        const rulesMap = {};
        marginRows.forEach((rule) => {
          // Extract the scope-specific value based on scope type
          let scopeValue = "";
          const scope = rule.scope?.toLowerCase() || "";

          if (scope === "item") {
            scopeValue = rule.itemSku || "";
          } else if (scope === "category") {
            scopeValue = rule.scopeValue || "";
          } else if (scope === "hq") {
            scopeValue = rule.scopeValue || "";
          } else if (scope === "group_code") {
            scopeValue = rule.scopeValue || rule.groupCode || rule.group_code || "";
          }

          const key = `${scope}_${scopeValue}`;
          rulesMap[key] = {
            scope: scope,
            scopeValue: scopeValue,
            strategy: strategyMap[rule.marginStrategy] || rule.marginStrategy?.toLowerCase() || "maintainMargin",
          };
        });

        // Store active rules for reference
        setFormState((prev) => ({
          ...prev,
          margin: {
            ...prev.margin,
            activeRules: rulesMap,
          },
        }));
      }

    } catch (error) {
      console.error("Error loading margin rules:", error);
      setMarginStrategies([]);
      setMarginTotalPages(0);
      setMarginTotalElements(0);
    } finally {
      setLoadingRuleConfig(false);
    }
  }, []);

  const loadSinglePriceRows = useCallback(async (nextPage = 0) => {
    setLoadingRuleConfig(true);
    try {
      const singlePriceData = await getSinglePriceRowsPage({ page: nextPage, size: 10 });
      const rows = Array.isArray(singlePriceData?.rows) ? singlePriceData.rows : [];

      setSinglePriceRows(rows);
      setSinglePricePage(Number(singlePriceData?.currentPage ?? nextPage));
      setSinglePriceTotalPages(Number(singlePriceData?.totalPages ?? 0));
      setSinglePriceTotalElements(Number(singlePriceData?.totalElements ?? rows.length));
    } catch (error) {
      console.error("Error loading single price rows:", error);
      setSinglePriceRows([]);
      setSinglePricePage(0);
      setSinglePriceTotalPages(0);
      setSinglePriceTotalElements(0);
    } finally {
      setLoadingRuleConfig(false);
    }
  }, []);

  useEffect(() => {
    if (activeRule === "margin") {
      void loadMarginRules(0);
    } else if (activeRule === "singlePrice") {
      void loadSinglePriceRows(0);
    } else if (activeRule === "landing" || activeRule === "prioritization") {
      loadRuleConfig(activeRule);
    } else {
      setLoadingRuleConfig(false);
    }
  }, [activeRule, loadRuleConfig, loadMarginRules, loadSinglePriceRows]);

  const handleMarginPageChange = useCallback(
    (nextPage) => {
      if (activeRule === "margin") {
        void loadMarginRules(nextPage);
      }
    },
    [activeRule, loadMarginRules]
  );

  const handleSinglePricePageChange = useCallback(
    (nextPage) => {
      if (activeRule === "singlePrice") {
        void loadSinglePriceRows(nextPage);
      }
    },
    [activeRule, loadSinglePriceRows]
  );

  const isMargin = activeRule === "margin";
  const isSinglePrice = activeRule === "singlePrice";
  const isRuleForm = activeRule === "landing" || activeRule === "prioritization";
  const showsMarginLogTable = isMargin;
  const config = FORM_CONFIG[activeRule] || {};
  const options = isRuleForm ? config.selectOptions || [] : [];
  const state = formState[activeRule] || {};
  const selectedOption =
    isRuleForm && options.length
      ? options.find((option) => option.value === state.selectedOption) || options[0]
      : {};
  const unit = isRuleForm ? selectedOption?.unit || "%" : "";
  const marginState = formState.margin || {};
  const { options: marginOptions, loading: loadingMarginOptions } = useMarginRuleOptions(
    marginState.scope,
    marginSearchTerm
  );
  const marginScopeValue = getMarginScopeValue(marginState);
  const selectedMarginOption = useMemo(
    () =>
      marginOptions.find((option) => getMarginScopeOptionValue(option) === marginScopeValue) ||
      (marginScopeValue ? { value: marginScopeValue, label: marginScopeValue } : null),
    [marginOptions, marginScopeValue]
  );
  const isMarginScopeSelected = Boolean(marginState.scope && marginState.scope !== "none");
  const thresholdValue = isRuleForm
    ? Object.prototype.hasOwnProperty.call(state, "value")
      ? state.value
      : selectedOption?.defaultValue ?? ""
    : "";
  const marginLogTableProps = isMargin
    ? {
        rows: marginStrategies,
        page: marginPage,
        pageCount: marginTotalPages,
        rowCount: marginTotalElements,
        onPageChange: handleMarginPageChange,
      }
    : {};

  const singlePriceLogTableProps = isSinglePrice
    ? {
        rows: singlePriceRows,
        page: singlePricePage,
        pageCount: singlePriceTotalPages,
        rowCount: singlePriceTotalElements,
        onPageChange: handleSinglePricePageChange,
      }
    : {};

  const handleRuleChange = (nextRule) => {
    setActiveRule(nextRule);

    if (nextRule === "landing") {
      setSearchParams({});
      return;
    }

    setSearchParams({ [ACTIVE_RULE_PARAM]: nextRule });
  };

  useEffect(() => {
    setMarginInputValue(getMarginScopeOptionLabel(selectedMarginOption) || marginScopeValue || "");
  }, [selectedMarginOption, marginScopeValue, marginState.scope]);

  const handleSelectChange = (event) => {
    const nextValue = event.target.value;
    const nextOption = options.find((option) => option.value === nextValue);
    setFormState((prev) => {
      const currentRuleState = prev[activeRule] || {};
      const valuesByRule = currentRuleState.valuesByRule || {};
      const mappedValue = getNumericValue(valuesByRule[nextValue]);
      const fallbackValue =
        getNumericValue(nextOption?.defaultValue) ?? getNumericValue(currentRuleState.value) ?? 0;

      return {
        ...prev,
        [activeRule]: {
          ...currentRuleState,
          selectedOption: nextValue,
          value: mappedValue ?? fallbackValue,
        },
      };
    });
  };

  const handleValueChange = (event) => {
    const rawValue = event.target.value;
    const sanitizedValue = rawValue.replace(/\D/g, "");
    const nextValue = sanitizedValue === "" ? "" : Number(sanitizedValue);
    setFormState((prev) => {
      const currentRuleState = prev[activeRule] || {};
      const selectedKey = currentRuleState.selectedOption;
      const valuesByRule = currentRuleState.valuesByRule || {};

      return {
        ...prev,
        [activeRule]: {
          ...currentRuleState,
          value: nextValue,
          valuesByRule:
            !isMargin && selectedKey
              ? nextValue === ""
                ? valuesByRule
                : {
                    ...valuesByRule,
                    [selectedKey]: nextValue,
                  }
              : valuesByRule,
        },
      };
    });
  };

  const resetMarginUpload = () => {
    setMarginUploadName("");
    if (marginFileInputRef.current) {
      marginFileInputRef.current.value = "";
    }
  };

  const resetSinglePriceUpload = () => {
    setSinglePriceUploadName("");
    if (singlePriceFileInputRef.current) {
      singlePriceFileInputRef.current.value = "";
    }
  };

  const handleMarginScopeChange = (event) => {
    const scope = event.target.value;
    setMarginSearchTerm("");
    setMarginInputValue("");
    setFormState((prev) => ({
      ...prev,
      margin: {
        ...prev.margin,
        scope,
        scopeValue: null,
        itemSku: null,
      },
    }));
  };

  const handleMarginScopeValueChange = (event, newValue) => {
    const selectedValue = getMarginScopeOptionValue(newValue);
    setMarginInputValue(getMarginScopeOptionLabel(newValue));
    setFormState((prev) => {
      const activeRules = prev.margin?.activeRules || {};
      const scope = prev.margin.scope;
      const key = `${scope}_${selectedValue}`;
      const activeRule = activeRules[key];
      const scopeField = getMarginScopeField(scope);

      return {
        ...prev,
        margin: {
          ...prev.margin,
          [scopeField]: selectedValue || null,
          strategy: activeRule?.strategy || prev.margin.strategy || "maintainMargin",
        },
      };
    });
  };

  const handleMarginStrategyChange = (event) => {
    const strategy = event.target.value;
    setFormState((prev) => ({
      ...prev,
      margin: {
        ...prev.margin,
        strategy,
      },
    }));
  };

  const handleMarginFileChange = (event) => {
    const file = event?.target?.files?.[0];
    const validationError = validateMarginUploadFile(file);

    if (validationError) {
      resetMarginUpload();
      AlertManager.error(validationError);
      return;
    }

    setMarginUploadName(file ? file.name : "");
  };

  const handleMarginTemplateDownload = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadMarginRuleTemplate();
    } catch (error) {
      console.error("Failed to download margin template", error);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleSinglePriceFileChange = (event) => {
    const file = event?.target?.files?.[0];
    const validationError = validateMarginUploadFile(file);

    if (validationError) {
      resetSinglePriceUpload();
      AlertManager.error(validationError);
      return;
    }

    setSinglePriceUploadName(file ? file.name : "");
  };

  const handleSinglePriceTemplateDownload = async () => {
    setDownloadingSinglePriceTemplate(true);
    try {
      await downloadSinglePriceTemplate();
    } catch (error) {
      console.error("Failed to download single price template", error);
      AlertManager.error("Unable to download the single price template.", {
        contactSupport: false,
      });
    } finally {
      setDownloadingSinglePriceTemplate(false);
    }
  };

  const handleSinglePriceUpload = async () => {
    const file = singlePriceFileInputRef.current?.files?.[0];

    if (!file) {
      AlertManager.error("Please select a file to upload.");
      return;
    }

    const validationError = validateMarginUploadFile(file);
    if (validationError) {
      resetSinglePriceUpload();
      AlertManager.error(validationError);
      return;
    }

    setUploadingSinglePriceFile(true);
    try {
      const response = await uploadSinglePriceFile(file);
      if (response) {
        AlertManager.success("Single price file uploaded successfully.", {
          contactSupport: false,
        });
        resetSinglePriceUpload();
        await loadSinglePriceRows(singlePricePage);
      } else {
        resetSinglePriceUpload();
      }
    } catch (error) {
      console.error("Error uploading single price file:", error);
      resetSinglePriceUpload();
      AlertManager.error(error?.message || "Failed to upload file. Please try again.");
    } finally {
      setUploadingSinglePriceFile(false);
    }
  };

  const handleMarginSave = async () => {
    // Validation
    if (!marginState.scope || marginState.scope === "none") {
      AlertManager.error("Please select a scope.");
      return;
    }

    const selectedScopeValue = getMarginScopeValue(marginState);

    if (!selectedScopeValue) {
      AlertManager.error("Please select a scope value.");
      return;
    }

    if (!marginState.strategy) {
      AlertManager.error("Please select a strategy.");
      return;
    }

    try {
      await AlertManager.confirm(
        "Are you sure you want to save this margin rule?",
        {
          contactSupport: false,
          contactSupportBtn: true,
          closeBtnText: "Save",
          contactBtnText: "Cancel",
        }
      );
    } catch (error) {
      return;
    }

    setSavingRule(true);
    try {
      // Transform strategy from camelCase to UPPER_CASE
      const strategyMap = {
        gpNeutral: "GP_NEUTRAL",
        maintainPrice: "MAINTAIN_PRICE",
        maintainMargin: "MAINTAIN_MARGIN",
        singlePrice: "SINGLE_PRICE",
      };

      const scope = marginState.scope.toUpperCase();
      const scopeField = getMarginScopeField(marginState.scope);

      // Build payload based on scope type
      const payload = {
        scope: scope,
        marginStrategy: strategyMap[marginState.strategy] || marginState.strategy.toUpperCase(),
      };

      payload[scopeField] = selectedScopeValue;

      const response = await updateMarginRule(payload);

      if (response) {
        // Reset form and refresh logs
        setFormState((prev) => ({
          ...prev,
          margin: {
            scope: "none",
            scopeValue: null,
            itemSku: null,
            strategy: "maintainMargin",
            activeRules: prev.margin?.activeRules || {},
          },
        }));
        setLogsRefreshKey((prev) => prev + 1);
        // Reload margin rules to get updated active rules
        await loadMarginRules();
      }
    } catch (error) {
      console.error("Error saving margin rule:", error);
    } finally {
      setSavingRule(false);
    }
  };

  const handleMarginUpload = async () => {
    const file = marginFileInputRef.current?.files?.[0];

    // Validation
    if (!file) {
      AlertManager.error("Please select a file to upload.");
      return;
    }

    const validationError = validateMarginUploadFile(file);
    if (validationError) {
      resetMarginUpload();
      AlertManager.error(validationError);
      return;
    }

    try {
      await AlertManager.confirm(
        `Are you sure you want to upload "${file.name}"?`,
        {
          contactSupport: false,
          contactSupportBtn: true,
          closeBtnText: "Upload",
          contactBtnText: "Cancel",
        }
      );
    } catch (error) {
      return;
    }

    setUploadingFile(true);
    try {
      const response = await uploadMarginRuleFile(file);
      if (response) {
        AlertManager.success("Bulk margin rules uploaded successfully.", {
          contactSupport: false,
        });

        // Reset upload and refresh logs
        resetMarginUpload();
        setLogsRefreshKey((prev) => prev + 1);
        await loadMarginRules(0);
      } else {
        resetMarginUpload();
      }
    } catch (error) {
      console.error("Error uploading margin rule file:", error);
      resetMarginUpload();
      AlertManager.error(error?.message || "Failed to upload file. Please try again.");
    } finally {
      setUploadingFile(false);
    }
  };
  const handleRuleSave = async () => {
    const activeOption =
      options.find((option) => option.value === state.selectedOption) ||
      selectedOption ||
      options[0] ||
      {};

    const selectedRuleKey = activeOption.value || state.selectedOption;
    const pendingValue =
      getNumericValue(state.value) ??
      getNumericValue(state.valuesByRule?.[selectedRuleKey]) ??
      getNumericValue(activeOption.defaultValue) ??
      0;
    const savedValuesByRule = state.savedValuesByRule || {};
    const savedValue = getNumericValue(savedValuesByRule[selectedRuleKey]);

    if (pendingValue !== null && savedValue !== null && pendingValue === savedValue) {
      AlertManager.warning("This rule is already using that value.", { contactSupport: false });
      return;
    }

    const seqIdByRule = state.seqIdByRule || {};
    const stateSeqId = state.seqId ?? null;
    const ruleNameByRule = state.ruleNameByRule || {};
    const activeSeqId =
      seqIdByRule[selectedRuleKey] ??
      seqIdByRule[activeOption.value || state.selectedOption] ??
      stateSeqId ??
      null;

    const formattedRuleFlag = activeRule.charAt(0).toUpperCase() + activeRule.slice(1);
    const activeRuleName =
      ruleNameByRule[activeOption.value || state.selectedOption] ??
      ruleNameByRule[state.selectedOption] ??
      activeOption.label ??
      selectedRuleKey ??
      `${formattedRuleFlag} rule`;

    const payload = {
      ruleFlag: activeRule === "prioritization" ? "prioritization" : formattedRuleFlag,
      ruleName: activeRuleName,
      valueText: String(pendingValue ?? activeOption.defaultValue ?? 0),
    };

    if (activeSeqId !== null && activeSeqId !== undefined) {
      payload.seqId = activeSeqId;
    }

    setSavingRule(true);
    try {
      const response = await updateRuleFlag(activeRule, payload);
      if (response) {
        if (pendingValue !== null && selectedRuleKey) {
          setFormState((prev) => ({
            ...prev,
            [activeRule]: {
              ...(prev[activeRule] || {}),
              savedValuesByRule: {
                ...((prev[activeRule] && prev[activeRule].savedValuesByRule) || {}),
                [selectedRuleKey]: pendingValue,
              },
            },
          }));
        }
        if (activeRule !== "margin") {
          await loadRuleConfig(activeRule, state.selectedOption);
        }
        setLogsRefreshKey((prev) => prev + 1);
      }
    } finally {
      setSavingRule(false);
    }
  };

  return (
    <section className={classes.page}>
      <header className={classes.header}>
        <div>
          <h1>Rules</h1>
          <p>Rules and thresholds that impact re-landing and re-pricing</p>
        </div>
      </header>

      <div className={classes.layout}>
        <div className={classes.leftColumn}>
          {RULES.map(({ key, title, grey }) => (
            <Button
              key={key}
              size="small"
              variant="contained"
              className={`${classes.ruleButton} ${
                activeRule === key ? classes.activeRuleButton : classes.inactiveRuleButton
              } ${grey ? classes.greyRuleButton : ""}`}
              onClick={() => handleRuleChange(key)}
              disabled={grey}
            >
              {title}
            </Button>
          ))}
        </div>

        <div className={classes.rightColumn}>
          {!isSinglePrice && (
            <section className={classes.formCard}>
            {isRuleForm && (
              <div className={classes.formRow}>
                <div className={classes.formField}>
                  <label className={classes.label}>Rule formula</label>
                  <Select
                    fullWidth
                    size="small"
                    value={state.selectedOption || selectedOption?.value || ""}
                    onChange={handleSelectChange}
                    className={classes.select}
                  >
                    {options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div className={classes.formField}>
                  <label className={classes.label}>Threshold</label>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={thresholdValue}
                    onChange={handleValueChange}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    InputProps={{
                      endAdornment: unit ? (
                        <InputAdornment position="end">{unit}</InputAdornment>
                      ) : null,
                    }}
                  />
                </div>

                <div className={`${classes.formField} ${classes.formActionField}`}>
                  <label className={classes.label}>&nbsp;</label>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    className={classes.saveButton}
                    onClick={handleRuleSave}
                    disabled={savingRule || loadingRuleConfig}
                  >
                    {savingRule ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            )}

            {isMargin && (
              <div className={classes.marginForm}>
                <div className={classes.marginControls}>
                  <div className={classes.formField}>
                    <label className={classes.label}>Scope</label>
                    <Select
                      fullWidth
                      size="small"
                      value={marginState.scope || "none"}
                      onChange={handleMarginScopeChange}
                      className={classes.select}
                    >
                      {MARGIN_SCOPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>

                  <div className={classes.formField}>
                    <label className={classes.label}>{getMarginScopeLabel(marginState.scope)}</label>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={marginOptions}
                      getOptionLabel={getMarginScopeOptionLabel}
                      isOptionEqualToValue={(option, value) =>
                        getMarginScopeOptionValue(option) === getMarginScopeOptionValue(value)
                      }
                      renderOption={(props, option) => <li {...props}>{getMarginScopeOptionLabel(option)}</li>}
                      value={selectedMarginOption}
                      inputValue={marginInputValue}
                      onChange={handleMarginScopeValueChange}
                      openOnFocus={isMarginScopeSelected}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "clear") {
                          setMarginSearchTerm("");
                          setMarginInputValue("");
                          return;
                        }

                        if (reason === "input") {
                          setMarginSearchTerm(newInputValue);
                          setMarginInputValue(newInputValue);
                          return;
                        }

                        if (reason === "selectOption" || reason === "reset") {
                          setMarginInputValue(newInputValue);
                        }
                      }}
                      loading={loadingMarginOptions}
                      disabled={false}
                      popupIcon={null}
                      filterOptions={(candidateOptions) => candidateOptions}
                      noOptionsText={
                        !isMarginScopeSelected
                          ? "Select a scope first"
                          : marginSearchTerm.trim().length < 2
                          ? "Type at least 2 characters to search"
                          : "No matching scope values"
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            !isMarginScopeSelected
                              ? "Select scope first"
                              : `Search ${getMarginScopeLabel(marginState.scope).toLowerCase()}`
                          }
                          helperText={!isMarginScopeSelected ? "Select a scope first" : ""}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                <InputAdornment position="end">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          inputProps={{
                            ...params.inputProps,
                            readOnly: !isMarginScopeSelected,
                          }}
                        />
                      )}
                      className={classes.autocomplete}
                    />
                  </div>

                  <div className={classes.formField}>
                    <label className={classes.label}>Strategy</label>
                    <Select
                      fullWidth
                      size="small"
                      value={marginState.strategy || "maintainMargin"}
                      onChange={handleMarginStrategyChange}
                      className={classes.select}
                    >
                      {MARGIN_STRATEGY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>

                  <div className={`${classes.formField} ${classes.formActionField}`}>
                    <label className={classes.label}>&nbsp;</label>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      className={classes.saveButton}
                      onClick={handleMarginSave}
                      disabled={savingRule || !marginState.scope || marginState.scope === "none" || !getMarginScopeValue(marginState)}
                    >
                      {savingRule ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>

                {!isMargin && (
                <form className={uploadClasses.container} data-margin-upload="true">
                  <Box className={uploadClasses.fieldRow}>
                    <Button component="label" variant="outlined" size="small">
                      Choose File
                      <input
                        ref={marginFileInputRef}
                        hidden
                        type="file"
                        accept={FILE_ACCEPTED_TYPES_WITH_CSV}
                        onChange={handleMarginFileChange}
                      />
                    </Button>
                    <Typography variant="body2" className={uploadClasses.fileNameDisplay}>
                      {marginUploadName || "No file selected"}
                    </Typography>
                    <Typography variant="body2" className={uploadClasses.helperText}>
                      Accepted formats: .xlsx / .xls / .csv · Max size {MARGIN_UPLOAD_MAX_SIZE_MB} MB
                    </Typography>
                  </Box>

                  <Box className={uploadClasses.actions}>
                    <Button
                      className={uploadClasses.uploadBtn}
                      type="button"
                      onClick={handleMarginUpload}
                      disabled={uploadingFile || !marginUploadName}
                    >
                      {uploadingFile ? "Uploading..." : "Upload"}
                    </Button>
                    <Tooltip title="Download the standardized margin strategy template">
                      <Button
                        className={uploadClasses.uploadBtn}
                        variant="text"
                        type="button"
                        onClick={handleMarginTemplateDownload}
                        disabled={downloadingTemplate}
                      >
                        {downloadingTemplate ? "Downloading..." : "Download Template"}
                      </Button>
                    </Tooltip>
                  </Box>
                </form>
                )}
              </div>
            )}

            </section>
          )}

          {isMargin ? (
            <>
              <form className={uploadClasses.container}>
                <Box className={uploadClasses.fieldRow}>
                  <Button component="label" variant="outlined" size="small">
                    Choose File
                    <input
                      ref={marginFileInputRef}
                      hidden
                      type="file"
                      accept={FILE_ACCEPTED_TYPES_WITH_CSV}
                      onChange={handleMarginFileChange}
                    />
                  </Button>
                  <Typography variant="body2" className={uploadClasses.fileNameDisplay}>
                    {marginUploadName || "No file selected"}
                  </Typography>
                  <Typography variant="body2" className={uploadClasses.helperText}>
                    Accepted formats: .xlsx / .xls / .csv Max size {MARGIN_UPLOAD_MAX_SIZE_MB} MB
                  </Typography>
                </Box>

                <Box className={uploadClasses.actions}>
                  <Button
                    className={uploadClasses.uploadBtn}
                    type="button"
                    onClick={handleMarginUpload}
                    disabled={uploadingFile || !marginUploadName}
                  >
                    {uploadingFile ? "Uploading..." : "Upload"}
                  </Button>
                  <Tooltip title="Download the standardized margin strategy template">
                    <Button
                      className={uploadClasses.uploadBtn}
                      variant="text"
                      type="button"
                      onClick={handleMarginTemplateDownload}
                      disabled={downloadingTemplate}
                    >
                      {downloadingTemplate ? "Downloading..." : "Download Template"}
                    </Button>
                  </Tooltip>
                </Box>
              </form>

              <div className={uploadClasses.hLine} />
            </>
          ) : null}

          {isSinglePrice ? (
            <>
              <form className={uploadClasses.container}>
                <Box className={uploadClasses.fieldRow}>
                  <Button component="label" variant="outlined" size="small">
                    Choose File
                    <input
                      ref={singlePriceFileInputRef}
                      hidden
                      type="file"
                      accept={FILE_ACCEPTED_TYPES_WITH_CSV}
                      onChange={handleSinglePriceFileChange}
                    />
                  </Button>
                  <Typography variant="body2" className={uploadClasses.fileNameDisplay}>
                    {singlePriceUploadName || "No file selected"}
                  </Typography>
                  <Typography variant="body2" className={uploadClasses.helperText}>
                    Accepted formats: .xlsx / .xls / .csv Max size 50 MB
                  </Typography>
                </Box>

                <Box className={uploadClasses.actions}>
                  <Button
                    className={uploadClasses.uploadBtn}
                    type="button"
                    onClick={handleSinglePriceUpload}
                    disabled={uploadingSinglePriceFile || !singlePriceUploadName}
                  >
                    {uploadingSinglePriceFile ? "Uploading..." : "Upload"}
                  </Button>
                  <Tooltip title="Download the standardized single price template">
                    <Button
                      className={uploadClasses.uploadBtn}
                      variant="text"
                      type="button"
                      onClick={handleSinglePriceTemplateDownload}
                      disabled={downloadingSinglePriceTemplate}
                    >
                      {downloadingSinglePriceTemplate ? "Downloading..." : "Download Template"}
                    </Button>
                  </Tooltip>
                </Box>
              </form>

              <div className={uploadClasses.hLine} />

              <h2 className={classes.logHeading}>Single price change log</h2>

              <MarginRuleLogTable
                refreshKey={logsRefreshKey}
                ruleType="singlePrice"
                pageSize={10}
                {...singlePriceLogTableProps}
              />
            </>
          ) : null}

          {activeRule === "landing" && (
            <div className={classes.logWrapper}>
              <h2 className={classes.logHeading}>Landing rule change log</h2>
              <LandingRuleLogTable refreshKey={logsRefreshKey} />
            </div>
          )}

          {activeRule === "prioritization" && (
          <div className={classes.logWrapper}>
              <h2 className={classes.logHeading}>Prioritization rule change log</h2>
              <PrioritizationRuleLogTable
                refreshKey={logsRefreshKey}
                rows={prioritizationLogRows}
                loading={activeRule === "prioritization" && loadingRuleConfig}
              />
            </div>
          )}

          {showsMarginLogTable && (
            <div className={classes.logWrapper}>
              <h2 className={classes.logHeading}>Margin strategy change log</h2>
              <MarginRuleLogTable
                refreshKey={logsRefreshKey}
                ruleType="margin"
                loading={loadingRuleConfig}
                pageSize={10}
                {...marginLogTableProps}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RulesPage;
